/**
 * Admin page for managing indicator visibility and calculated indicators
 */

import { useState, useEffect } from 'react'
import { Search, Loader2, Eye, EyeOff, ChevronLeft, ChevronRight, Plus, Pencil, Trash2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import {
  useAdminIndicators,
  useToggleVisibility,
  useBulkVisibility,
  useCalculatedIndicators,
  useDeleteCalculatedIndicator,
} from '../hooks/indicatorQueries'
import type { IndicatorAdminInfo, CalculatedIndicatorResponse } from '../services/indicatorApi'
import { FormulaBuilder } from '../components/FormulaBuilder'

const PAGE_SIZE = 100

const SOURCE_OPTIONS = [
  { value: '', label: 'Alle Quellen' },
  { value: 'inkar', label: 'INKAR' },
  { value: 'regionalstatistik', label: 'Regionalstatistik' },
  { value: 'calculated', label: 'Berechnet' },
  { value: 'csv_import', label: 'CSV-Import' },
  { value: 'deutschlandatlas', label: 'Deutschlandatlas' },
  { value: 'gv100', label: 'Stammdaten (GV100)' },
]

const VISIBILITY_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: 'true', label: 'Sichtbar' },
  { value: 'false', label: 'Versteckt' },
]

export function IndicatorsPage() {
  const [activeTab, setActiveTab] = useState<'indicators' | 'calculated'>('indicators')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gut8-anthracite">Indikatoren</h1>
        <p className="text-text-muted mt-1">Sichtbarkeit und berechnete Indikatoren verwalten</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border-gut8-light">
        <button
          onClick={() => setActiveTab('indicators')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'indicators'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-muted hover:text-gut8-anthracite'
          )}
        >
          Indikatoren
        </button>
        <button
          onClick={() => setActiveTab('calculated')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'calculated'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-muted hover:text-gut8-anthracite'
          )}
        >
          Berechnete Indikatoren
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'indicators' ? <IndicatorsTab /> : <CalculatedTab />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab 2: Calculated Indicators
// ---------------------------------------------------------------------------

const FORMULA_TYPE_LABELS: Record<string, string> = {
  arithmetic: 'Arithmetisch',
  quantile_rank: 'Quantil-Rang',
  weighted_rank: 'Gewichteter Rang',
  weighted_composite: 'Kompositwert',
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  municipality: 'Gemeinde',
  county: 'Kreis',
  government_district: 'Reg.-Bezirk',
  state: 'Land',
  federal: 'Bund',
}

function CalculatedTab() {
  const { data, isLoading, isError } = useCalculatedIndicators()
  const deleteMut = useDeleteCalculatedIndicator()
  const toggleVis = useToggleVisibility()

  const items = data?.items ?? []

  // Modal state
  const [showBuilder, setShowBuilder] = useState(false)
  const [editId, setEditId] = useState<number | undefined>(undefined)

  function openCreate() {
    setEditId(undefined)
    setShowBuilder(true)
  }

  function openEdit(id: number) {
    setEditId(id)
    setShowBuilder(true)
  }

  function handleDelete(ci: CalculatedIndicatorResponse) {
    if (!window.confirm(`"${ci.name}" wirklich loeschen? Diese Aktion kann nicht rueckgaengig gemacht werden.`)) return
    deleteMut.mutate(ci.id)
  }

  function handleSaved() {
    setShowBuilder(false)
    setEditId(undefined)
  }

  return (
    <div className="space-y-4">
      {/* Header with create button */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-text-muted">
          {items.length} berechnete{items.length === 1 ? 'r' : ''} Indikator{items.length === 1 ? '' : 'en'}
        </span>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary text-white rounded-gut8 hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Neu erstellen
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
          <span className="ml-2 text-text-muted text-sm">Berechnete Indikatoren werden geladen...</span>
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-gut8 p-4 text-sm text-red-700">
          Fehler beim Laden der berechneten Indikatoren.
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && items.length === 0 && (
        <div className="bg-gut8-bg-muted rounded-gut8 border border-border-gut8-light p-12 text-center">
          <p className="text-text-muted mb-3">Noch keine berechneten Indikatoren vorhanden.</p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-primary text-white rounded-gut8 hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ersten Indikator erstellen
          </button>
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && items.length > 0 && (
        <div className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-gut8-light bg-gut8-bg-muted">
                  <th className="px-3 py-3 text-left font-medium text-gut8-anthracite">Name</th>
                  <th className="px-3 py-3 text-left font-medium text-gut8-anthracite">Formeltyp</th>
                  <th className="px-3 py-3 text-left font-medium text-gut8-anthracite">Entity-Types</th>
                  <th className="px-3 py-3 text-center font-medium text-gut8-anthracite">Sichtbar</th>
                  <th className="px-3 py-3 text-right font-medium text-gut8-anthracite">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {items.map((ci) => (
                  <tr
                    key={ci.id}
                    className="border-b border-border-gut8-light last:border-b-0 hover:bg-gut8-bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => openEdit(ci.id)}
                  >
                    <td className="px-3 py-2.5 text-gut8-anthracite font-medium max-w-xs truncate" title={ci.name}>
                      {ci.name}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        {FORMULA_TYPE_LABELS[ci.formula_type] ?? ci.formula_type}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {ci.entity_types.map((et) => (
                          <span key={et} className="inline-flex px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            {ENTITY_TYPE_LABELS[et] ?? et}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleVis.mutate({ id: ci.indicator_id, isVisible: !ci.is_visible })
                        }}
                        className={cn(
                          'w-10 h-6 rounded-full transition-colors relative inline-block',
                          ci.is_visible ? 'bg-green-500' : 'bg-gray-300'
                        )}
                        title={ci.is_visible ? 'Sichtbar' : 'Versteckt'}
                      >
                        <span
                          className={cn(
                            'absolute top-1 w-4 h-4 rounded-full bg-white transition-transform',
                            ci.is_visible ? 'left-5' : 'left-1'
                          )}
                        />
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            openEdit(ci.id)
                          }}
                          className="p-1.5 rounded-gut8 text-text-muted hover:text-primary hover:bg-primary-subtle transition-colors"
                          title="Bearbeiten"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDelete(ci)
                          }}
                          disabled={deleteMut.isPending}
                          className="p-1.5 rounded-gut8 text-text-muted hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                          title="Loeschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Formula Builder Modal */}
      {showBuilder && (
        <FormulaBuilder
          editId={editId}
          onClose={() => { setShowBuilder(false); setEditId(undefined) }}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab 1: Indicators with visibility management
// ---------------------------------------------------------------------------

function IndicatorsTab() {
  // Search with debounce
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Filters
  const [sourceFilter, setSourceFilter] = useState('')
  const [visibilityFilter, setVisibilityFilter] = useState('')

  // Pagination
  const [offset, setOffset] = useState(0)

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0)
  }, [search, sourceFilter, visibilityFilter])

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Query
  const { data, isLoading, isError } = useAdminIndicators({
    source: sourceFilter || undefined,
    is_visible: visibilityFilter === '' ? undefined : visibilityFilter === 'true',
    search: search || undefined,
    limit: PAGE_SIZE,
    offset,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0

  // Mutations
  const toggleVisibility = useToggleVisibility()
  const bulkVisibility = useBulkVisibility()

  // Clear selection when data changes
  useEffect(() => {
    setSelectedIds(new Set())
  }, [data])

  // Selection helpers
  const allSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id))
  const someSelected = selectedIds.size > 0

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)))
    }
  }

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function handleBulkVisibility(isVisible: boolean) {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    bulkVisibility.mutate({ ids, isVisible }, {
      onSuccess: () => setSelectedIds(new Set()),
    })
  }

  // Pagination info
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1
  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="space-y-4">
      {/* Search & Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Indikator suchen..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>

        {/* Source filter */}
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        >
          {SOURCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {/* Visibility filter */}
        <select
          value={visibilityFilter}
          onChange={(e) => setVisibilityFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        >
          {VISIBILITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
          <span className="ml-2 text-text-muted text-sm">Indikatoren werden geladen...</span>
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-gut8 p-4 text-sm text-red-700">
          Fehler beim Laden der Indikatoren.
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && items.length === 0 && (
        <div className="bg-gut8-bg-muted rounded-gut8 border border-border-gut8-light p-12 text-center">
          <p className="text-text-muted">Keine Indikatoren gefunden.</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && items.length > 0 && (
        <div className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-gut8-light bg-gut8-bg-muted">
                  <th className="w-10 px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-border-gut8-medium"
                    />
                  </th>
                  <th className="px-3 py-3 text-left font-medium text-gut8-anthracite">Name</th>
                  <th className="px-3 py-3 text-left font-medium text-gut8-anthracite">Code</th>
                  <th className="px-3 py-3 text-left font-medium text-gut8-anthracite">Quelle</th>
                  <th className="px-3 py-3 text-left font-medium text-gut8-anthracite">Kategorie</th>
                  <th className="px-3 py-3 text-left font-medium text-gut8-anthracite">Einheit</th>
                  <th className="px-3 py-3 text-center font-medium text-gut8-anthracite">Sichtbar</th>
                </tr>
              </thead>
              <tbody>
                {items.map((ind) => (
                  <IndicatorRow
                    key={ind.id}
                    indicator={ind}
                    selected={selectedIds.has(ind.id)}
                    onToggleSelect={() => toggleSelect(ind.id)}
                    onToggleVisibility={() =>
                      toggleVisibility.mutate({ id: ind.id, isVisible: !ind.is_visible })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border-gut8-light bg-gut8-bg-muted">
              <span className="text-sm text-text-muted">
                {offset + 1}&ndash;{Math.min(offset + PAGE_SIZE, total)} von {total} Indikatoren
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                  className="p-1 rounded-gut8 border border-border-gut8-medium bg-white disabled:opacity-40 hover:bg-gut8-bg-muted"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gut8-anthracite">
                  Seite {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={offset + PAGE_SIZE >= total}
                  className="p-1 rounded-gut8 border border-border-gut8-medium bg-white disabled:opacity-40 hover:bg-gut8-bg-muted"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk action bar */}
      {someSelected && (
        <div className="sticky bottom-4 flex items-center justify-between bg-gut8-anthracite text-white rounded-gut8 px-4 py-3 shadow-gut8-sm">
          <span className="text-sm font-medium">{selectedIds.size} ausgew&auml;hlt</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleBulkVisibility(true)}
              disabled={bulkVisibility.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 rounded-gut8 transition-colors disabled:opacity-50"
            >
              <Eye className="w-4 h-4" />
              Sichtbar machen
            </button>
            <button
              onClick={() => handleBulkVisibility(false)}
              disabled={bulkVisibility.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-600 hover:bg-gray-700 rounded-gut8 transition-colors disabled:opacity-50"
            >
              <EyeOff className="w-4 h-4" />
              Verstecken
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Table row
// ---------------------------------------------------------------------------

function IndicatorRow({
  indicator,
  selected,
  onToggleSelect,
  onToggleVisibility,
}: {
  indicator: IndicatorAdminInfo
  selected: boolean
  onToggleSelect: () => void
  onToggleVisibility: () => void
}) {
  return (
    <tr className={cn(
      'border-b border-border-gut8-light last:border-b-0 transition-colors',
      selected ? 'bg-primary/5' : 'hover:bg-gut8-bg-muted/50'
    )}>
      <td className="px-3 py-2.5">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="rounded border-border-gut8-medium"
        />
      </td>
      <td className="px-3 py-2.5 text-gut8-anthracite font-medium max-w-xs truncate" title={indicator.name}>
        {indicator.name}
      </td>
      <td className="px-3 py-2.5 text-text-muted font-mono text-xs">
        {indicator.code}
      </td>
      <td className="px-3 py-2.5">
        <SourceBadge source={indicator.source} isCalculated={indicator.is_calculated} />
      </td>
      <td className="px-3 py-2.5 text-text-muted text-xs max-w-[160px] truncate" title={indicator.category ?? ''}>
        {indicator.category ?? '\u2014'}
      </td>
      <td className="px-3 py-2.5 text-text-muted text-xs">
        {indicator.unit ?? '\u2014'}
      </td>
      <td className="px-3 py-2.5 text-center">
        <button
          onClick={onToggleVisibility}
          className={cn(
            'w-10 h-6 rounded-full transition-colors relative inline-block',
            indicator.is_visible ? 'bg-green-500' : 'bg-gray-300'
          )}
          title={indicator.is_visible ? 'Sichtbar' : 'Versteckt'}
        >
          <span
            className={cn(
              'absolute top-1 w-4 h-4 rounded-full bg-white transition-transform',
              indicator.is_visible ? 'left-5' : 'left-1'
            )}
          />
        </button>
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Source badge
// ---------------------------------------------------------------------------

function SourceBadge({ source, isCalculated }: { source: string | null; isCalculated: boolean }) {
  if (isCalculated) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
        Berechnet
      </span>
    )
  }

  switch (source) {
    case 'inkar':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          INKAR
        </span>
      )
    case 'regionalstatistik':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          Regionalstatistik
        </span>
      )
    case 'csv_import':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          CSV-Import
        </span>
      )
    case 'deutschlandatlas':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
          Deutschlandatlas
        </span>
      )
    case 'gv100':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
          Stammdaten
        </span>
      )
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          {source ?? 'Unbekannt'}
        </span>
      )
  }
}
